import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const STORAGE_KEY = "er_theme_preference";
export const THEME_OPTIONS = ["light", "dark", "system"];

const ThemeContext = createContext({
  preference: "light",
  resolved: "light",
  setPreference: () => {},
  loading: false,
});

function readStoredPreference() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (THEME_OPTIONS.includes(stored)) return stored;
  } catch {
    // ignore — sandbox/iframe contexts may block localStorage
  }
  return "light";
}

function resolvePreference(preference) {
  if (preference === "system") {
    if (typeof window !== "undefined" && window.matchMedia) {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return "light";
  }
  return preference;
}

function applyThemeClass(resolved) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.style.colorScheme = resolved === "dark" ? "dark" : "light";
}

export function ThemeProvider({ children }) {
  const { user, refreshMe } = useAuth();
  // bootstrap from server preference (if user is loaded) → localStorage → 'light'
  const [preference, setPreferenceState] = useState(
    () => user?.theme_preference || readStoredPreference()
  );
  const [saving, setSaving] = useState(false);

  // sync from server when user changes (login / token refresh)
  useEffect(() => {
    if (user?.theme_preference) {
      setPreferenceState(user.theme_preference);
      try {
        localStorage.setItem(STORAGE_KEY, user.theme_preference);
      } catch {
        // ignore
      }
    }
  }, [user?.theme_preference]);

  // resolve + apply
  const resolved = useMemo(() => resolvePreference(preference), [preference]);
  useEffect(() => {
    applyThemeClass(resolved);
  }, [resolved]);

  // when preference is "system", listen for OS changes live
  useEffect(() => {
    if (preference !== "system") return undefined;
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => applyThemeClass(e.matches ? "dark" : "light");
    if (mql.addEventListener) mql.addEventListener("change", handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", handler);
      else mql.removeListener(handler);
    };
  }, [preference]);

  const setPreference = useCallback(
    async (next) => {
      if (!THEME_OPTIONS.includes(next)) return;
      setPreferenceState(next);
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // ignore
      }
      // persist server-side if logged in
      if (user?.id) {
        try {
          setSaving(true);
          await api.patch("/auth/profile", { theme_preference: next });
          if (refreshMe) await refreshMe();
        } catch (err) {
          // Non-fatal — local state still updated. The next login will resync.
          console.warn("Failed to persist theme_preference:", err?.message);
        } finally {
          setSaving(false);
        }
      }
    },
    [user?.id, refreshMe]
  );

  const value = useMemo(
    () => ({ preference, resolved, setPreference, loading: saving }),
    [preference, resolved, setPreference, saving]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
